import React from 'react';
import {
  breakpointSizesMap,
  cloudinaryUtils,
  Headline,
  headlineClasses,
  LeadParagraph,
  Picture,
  Skeleton,
  useCloudinary,
} from '@sato/components';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';

import { IWidgetFields } from '@/contentful/types';

import { ResidentTipsList, TipsProps } from '@components/ResidentTipsList';
import { useCustomTranslations } from '@hooks/useCustomTranslations';
import { useApartment } from '@stores/apartmentStore';
import { contractStore } from '@stores/contractStore';
import { userStore } from '@stores/userStore';

export type MyHomeHeaderWidgetParameters = {
  newOrUpcomingResidentsTips: TipsProps;
  leavingResidentsTips: TipsProps;
};

/**
 * Main header of the user front page. Defined as a widget in Contentful
 *
 * @link https://app.contentful.com/spaces/j0g97d8kbhf7/entries/71QaEItuIqkNBxB5Ls4iO5
 */
export const MyHomeHeader = observer(
  ({ customTranslations, parameters }: IWidgetFields) => {
    const { t } = useCustomTranslations(customTranslations);
    const { apartment } = useApartment();
    const cld = useCloudinary();

    const isNewOrUpcomingContract = contractStore.isContractNewOrUpcoming;
    const isContractTerminated = contractStore.isContractTerminated;
    const isContractEnded = contractStore.isContractEnded;

    const firstName = userStore.user?.firstName;

    const apartmentImageUrl = apartment?.buildingImageURL
      ? cloudinaryUtils
          .getCloudinaryImage({
            cld,
            imageId: apartment.buildingImageURL,
            isFullScreen: false,
            quality: 'good',
            imageFormat: 'avif',
            width: breakpointSizesMap.lg,
          })
          .toURL()
      : undefined;

    const renderTips = () => {
      if (isContractTerminated) {
        return (
          <ResidentTipsList
            className="mt-4 lg:mb-4"
            heading={t('leavingResidentsListTitle')}
            buttonText={t('allTipsButtonText')}
            {...(parameters as MyHomeHeaderWidgetParameters)
              .leavingResidentsTips}
          />
        );
      }

      if (isNewOrUpcomingContract) {
        return (
          <ResidentTipsList
            className="mt-4 lg:mb-4"
            heading={t('newResidentsListTitle')}
            buttonText={t('allTipsButtonText')}
            {...(parameters as MyHomeHeaderWidgetParameters)
              .newOrUpcomingResidentsTips}
          />
        );
      }

      return null;
    };

    const renderTitle = () => {
      if (isContractEnded) {
        return t('titleContractHasEnded');
      } else if (isNewOrUpcomingContract) {
        return t('titleNewResident');
      }

      return t('title');
    };

    return (
      <header className="container-left-half -mb-8 w-full items-center bg-brand-primary-main lg:-mb-9">
        <div
          className={classNames(
            'container-content flex flex-col justify-center mdDown:text-center',
            'pb-5 pt-3 sm:pt-4 md:min-h-[20rem] md:pb-6 md:pt-7 lg:min-h-[26rem] xl:pr-7'
          )}
        >
          <Headline
            level={1}
            className="mb-1 flex flex-wrap gap-x-2 text-white antialiased md:mb-3 lgDown:flex-col"
          >
            {firstName ? (
              <>
                <span>{renderTitle()},</span>
                <span>{firstName}!</span>
              </>
            ) : (
              <Skeleton
                className={classNames(
                  'h-[1.1em] rounded *:!bg-transparent',
                  headlineClasses[1]
                )}
              />
            )}
          </Headline>

          <LeadParagraph className="text-white antialiased">
            {isContractEnded
              ? t('subHeadingContractHasEnded')
              : t('subHeading')}
          </LeadParagraph>

          {renderTips()}
        </div>

        <div className="container-side-content relative h-full w-full lgDown:aspect-video">
          <Picture
            src={apartmentImageUrl}
            className="absolute inset-0"
            loading="eager"
            fill
          />
        </div>
      </header>
    );
  }
);
